import produce from 'immer';
import reactUpdate from 'immutability-helper';

import BenchlingConfig from 'BenchlingConfig';
import {EventConstants} from 'constants/EventConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {ToServerId} from 'notebook/ELNConstants';
import BioentityNoteUpdater from 'notebook/notes/BioentityNoteUpdater';
import * as NoteStoreTransforms from 'notebook/notes/NoteStoreTransforms';
import VersionedItemSubstore, {ItemEditorState, ItemVersionState} from 'notebook/store/VersionedItemSubstore';
import {removeQueryResolutionsForTableFromSnapshot} from 'notebook/tables/excess/Resolver';
import {
  getUpdatedNoteWithValidationErrors,
  getDocumentWithUpdatedValidationSnapshot,
} from 'notebook/tables/excess/ValidationErrorHandler';
import {FocusedTableViewMode} from 'notebook/tables/TableConstants';
import {createNoteRuntimeSnapshot} from 'notebook/tables/TableStoreTransforms';
import {
  BioentityNote,
  BioentityServerNote,
  BioentityServerNoteVersion,
  NotePart,
  ServerNote,
  ToServerNotePart,
} from 'notebook/util/NoteUtilConstants';
// @ts-expect-error
import TrayTaskStore from 'tray/TrayTaskStore';

class NoteStore extends Store {
  itemSubstore!: VersionedItemSubstore<
    BioentityNote,
    NotePart,
    BioentityServerNote,
    BioentityServerNoteVersion,
    'note',
    'fileId'
  >;

  postSanitizeItem(
    id: string,
    newNote: BioentityNote,
    oldNote: BioentityNote,
    {
      bumpVersion,
      performTableEvaluation = true,
      skipValidationStalenessCheck = false,
    }: {
      bumpVersion?: boolean;
      performTableEvaluation?: boolean;
      skipValidationStalenessCheck: boolean;
    }
  ): BioentityNote {
    // TODO(BNCH-30610): Remove this edge case once persist eval has been fully rolled out (when ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL will always be true)
    if (!BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL) {
      // If Excess Version is null we can no-op since it signifies an empty NRS
      if (newNote.noteRuntimeSnapshot.excessVersion == null) {
        return newNote;
      }
      const emptySnapshot = createNoteRuntimeSnapshot();
      return {
        ...newNote,
        noteRuntimeSnapshot: emptySnapshot,
      };
    }
    if (!performTableEvaluation) {
      return newNote;
    }

    const updater = new BioentityNoteUpdater(
      id,
      () => this.itemSubstore.getItem(id),
      (noteVersion, asyncMultidocTableOp) => {
        const NoteActions = require('actions/NoteActions').default;
        NoteActions.update(
          id,
          {
            $merge: getDocumentWithUpdatedValidationSnapshot({
              oldDocument: oldNote,
              newDocument: noteVersion,
              getNote: (note) => note,
              skipValidationStalenessCheck,
            }),
          },
          {bumpVersion} // TODO(BNCH-137948): Table op support for bioentity notes.
        );
      }
    );

    return getDocumentWithUpdatedValidationSnapshot({
      oldDocument: oldNote,
      newDocument: updater.run(newNote, oldNote),
      getNote: (note) => note,
      skipValidationStalenessCheck,
    });
  }

  override resetState(): void {
    if (this.itemSubstore == null) {
      this.itemSubstore = new VersionedItemSubstore({
        options: {
          idKey: 'fileId',
          itemKey: 'note',
          asyncItemLoad: false,
          sanitizeItem: NoteStoreTransforms.sanitizeNote,
          postSanitizeItem: this.postSanitizeItem.bind(this),
          postProcess(versionedNote: ItemEditorState<BioentityNote, NotePart, 'note', 'fileId'>) {
            if (versionedNote.note != null) {
              const newNote = NoteStoreTransforms.addTasksToNote(versionedNote.note);
              return reactUpdate(versionedNote, {note: {$set: newNote}});
            } else {
              return versionedNote;
            }
          },
          invertPostProcess: NoteStoreTransforms.removeTasksFromNote,
          prepareForStore: NoteStoreTransforms.prepareForStore,
          pickRevertableFields: NoteStoreTransforms.pickRevertableFields,
          doSave(id: string) {
            const NoteActions = require('actions/NoteActions').default;
            return NoteActions.save(id);
          },
        },
        events: {
          item: {
            load: EventConstants.NOTE_LOAD,
            registerObjects: EventConstants.NOTE_REGISTER_OBJECTS,
            bulkUpdate: EventConstants.NOTE_BULK_UPDATE,
            close: EventConstants.NOTE_CLOSE,
            rename: EventConstants.FILE_BROWSER_RENAME_FILE,
            revert: EventConstants.NOTE_REPLACE,
            update: EventConstants.NOTE_UPDATE,
            save: {
              init: EventConstants.NOTE_SAVE,
              completed: EventConstants.NOTE_SAVE_COMPLETED,
              failed: EventConstants.NOTE_SAVE_FAILED,
            },
          },
          version: {
            load: {
              init: EventConstants.NOTE_LOAD_VERSION,
              completed: EventConstants.NOTE_LOAD_VERSION_COMPLETED,
              failed: EventConstants.NOTE_LOAD_VERSION_FAILED,
            },
          },
          versions: {
            fetch: {
              init: EventConstants.NOTE_FETCH_VERSIONS,
              completed: EventConstants.NOTE_FETCH_VERSIONS_COMPLETED,
              failed: EventConstants.NOTE_FETCH_VERSIONS_FAILED,
            },
          },
        },
      });
    }
    this.itemSubstore.resetState();
  }

  inStore(id: string): boolean {
    return this.itemSubstore.inStore(id);
  }

  isUnsaved(id: string): boolean {
    return this.itemSubstore.isUnsaved(id);
  }

  getEditorState(id: string): ItemEditorState<BioentityNote, NotePart, 'note', 'fileId'> {
    return this.itemSubstore.getEditorState(id);
  }

  getVersionState(id: string): ItemVersionState<BioentityServerNoteVersion> {
    return this.itemSubstore.getVersionState(id);
  }

  getIdFromCid(id: string, cid: string): string | number | undefined {
    return this.itemSubstore.getIdFromCid(id, cid);
  }

  getNotePartsWithIds(fileId: string, cid: string): Array<ToServerNotePart> {
    const {cidIdMap, note} = this.itemSubstore.getItemStateUnsafe(fileId);
    const noteParts = (() => {
      const result: Array<ToServerNotePart> = [];
      for (const notePart of Array.from(note.noteParts)) {
        const id = cidIdMap[notePart.cid];
        if (id == null) {
          continue;
        }
        result.push({...notePart, id});
      }
      return result;
    })();
    return noteParts;
  }

  hasUnsavedChanges(id: string): boolean {
    return this.itemSubstore.hasUnsavedChanges(id);
  }

  prepareForSave(id: string, note: BioentityNote): ServerNote<ToServerId> {
    const {cidIdMap} = this.itemSubstore.getItemStateUnsafe(id);
    return NoteStoreTransforms.prepareForSave(note, cidIdMap, id);
  }

  override payloadHandler(action: FluxAction): boolean {
    let shouldEmit = false;

    // @ts-expect-error ts(2345)
    if (this.itemSubstore.handlePayload(action)) {
      shouldEmit = true;
    }

    switch (action.actionType) {
      case EventConstants.TRAY_TASK_UPDATE: {
        AppDispatcher.waitFor([TrayTaskStore.dispatchIndex]);
        shouldEmit = true;
        break;
      }
      case EventConstants.EXCESS_DELETE_SNAPSHOTTED_RESOLUTIONS_FOR_TABLE: {
        const {itemId, provenanceUuid, tableOp, lookupConfigIds} = action;
        if (!this.itemSubstore.inStore(itemId)) {
          return true;
        }
        const {note} = this.itemSubstore.getItemStateUnsafe(itemId);

        const updatedNote = {
          ...note,
          noteRuntimeSnapshot: removeQueryResolutionsForTableFromSnapshot(
            note.noteRuntimeSnapshot,
            provenanceUuid,
            lookupConfigIds
          ),
        };

        this.itemSubstore.updateItem(itemId, updatedNote, {
          bumpVersion: true,
          multidocTableOp: {[itemId]: tableOp},
        });
        shouldEmit = true;
        break;
      }
      case EventConstants.NOTE_SAVE_COMPLETED: {
        // propagate any new note part provenance IDs to the store
        const {fileId, cidNotePartProvenanceIdMap} = action;
        const note = this.itemSubstore.getItemState(fileId)?.note;
        if (note == null) {
          // If the user has closed the tab at this point, then the note is no longer in the store
          // even though the save action is still in progress. We can safely break here since there is
          // no need to update the note in the store in this case.
          break;
        }
        const updatedNote = produce(note, (draft: {noteParts: Array<NotePart>}) => {
          draft.noteParts.forEach((notePart) => {
            if (
              cidNotePartProvenanceIdMap &&
              cidNotePartProvenanceIdMap[notePart.cid] != null &&
              notePart.provenanceId !== cidNotePartProvenanceIdMap[notePart.cid]
            ) {
              notePart.provenanceId = cidNotePartProvenanceIdMap[notePart.cid];
            }
          });
        });
        this.itemSubstore.updateItem(fileId, updatedNote, {
          bumpVersion: false,
          performTableEvaluation: false,
        });
        shouldEmit = true;
        break;
      }
      case EventConstants.ENTRY_SET_FOCUSED_TABLE: {
        const {fileId, focusedTableProvenanceUuid, focusedTableViewMode} = action;
        if (!this.itemSubstore.inStore(fileId)) {
          break;
        }
        this.itemSubstore.mergeItem(fileId, {
          focusedTableProvenanceUuid,
          focusedTableViewMode: focusedTableViewMode ?? FocusedTableViewMode.FULLSCREEN,
        });
        shouldEmit = true;
        break;
      }
      case EventConstants.STRUCTURED_TABLE_VALIDATION_ERRORS: {
        const {entryId, validationErrorResponse} = action;
        if (!this.itemSubstore.inStore(entryId)) {
          break;
        }
        const {note} = this.itemSubstore.getItemStateUnsafe(entryId);
        const newNote = getUpdatedNoteWithValidationErrors(note, validationErrorResponse);
        this.itemSubstore.mergeItem(entryId, newNote, {bumpVersion: true});
        shouldEmit = true;
        break;
      }
      default:
        break;
    }

    if (shouldEmit) {
      this.emitChange();
    }

    return true;
  }

  __serverBootstrapItem(object: {id: string; item: BioentityServerNote}): void {
    this.itemSubstore.__serverBootstrapItem(object);
  }
}

const noteStore = new NoteStore(AppDispatcher);
export default noteStore;
